import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "react-bootstrap";
import dayjs from "dayjs";
import { useAuth } from "../../../app/providers/AuthProvider";
import { usePlannings } from "../../teacher/hooks/usePlannings";
import { StatCard, QuickAction, SectionTitle, DashboardGreeting } from "../components/DashboardKit";
import { formatTime12h } from "../../../utils/formatTime";

function TeacherDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const name = user?.name || user?.email || "Docente";
  const base = `/${slug}/app`;

  const { data: plannings } = usePlannings();

  const { upcoming, nextClass, todayCount } = useMemo(() => {
    const list = plannings ?? [];
    const today = dayjs().startOf("day");
    const future = list
      .filter((p) => dayjs(p.date).isAfter(today) || dayjs(p.date).isSame(today))
      .sort((a, b) => dayjs(a.date) - dayjs(b.date));
    return {
      upcoming: future,
      nextClass: future[0] ?? null,
      todayCount: list.filter((p) => dayjs(p.date).isSame(today, "day")).length,
    };
  }, [plannings]);

  const topicOf = (p) =>
    (p?.topics ?? []).map((t) => t.name ?? t).join(", ") || p?.title || "Clase";

  return (
    <div className="container-fluid px-0 py-3">
      <DashboardGreeting
        name={name}
        subtitle="Panel del docente — tu agenda y tus clases de un vistazo."
      />

      <div className="row g-3">
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-calendar-multiple"
            variant="primary"
            label="Clases programadas"
            value={(plannings ?? []).length}
            to={`${base}/agenda`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-calendar-clock"
            variant="info"
            label="Próximas"
            value={upcoming.length}
            to={`${base}/agenda`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-calendar-today"
            variant="success"
            label="Hoy"
            value={todayCount}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-clock-outline"
            variant="warning"
            label="Mis horas"
            value="Ver"
            to={`${base}/mis-horas`}
          />
        </div>
      </div>

      <SectionTitle>Próxima clase</SectionTitle>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {nextClass ? (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="fw-semibold">{topicOf(nextClass)}</div>
                <div className="text-muted small">
                  {dayjs(nextClass.date).format("dddd D [de] MMMM")}
                  {nextClass.time_start ? ` · ${formatTime12h(nextClass.time_start)}` : ""}
                  {nextClass.duration ? ` · ${nextClass.duration} h` : ""}
                  {nextClass.place ? ` · ${nextClass.place}` : ""}
                </div>
                <div className="text-muted small">
                  {nextClass.enrolled_count ?? 0} inscrito(s)
                </div>
              </div>
              <Link
                to={`${base}/agenda/${nextClass.id}/grade-card`}
                className="btn btn-primary"
                style={{ minHeight: 44 }}
              >
                Asistencia y calificación →
              </Link>
            </div>
          ) : (
            <p className="text-muted mb-0">
              No tienes clases próximas.{" "}
              <Link to={`${base}/agenda?nueva=1`}>Programa una clase</Link>.
            </p>
          )}
        </Card.Body>
      </Card>

      <SectionTitle>Accesos rápidos</SectionTitle>
      <div className="row g-2">
        <div className="col-6 col-md-4">
          <QuickAction to={`${base}/agenda?nueva=1`} icon="mdi-calendar-plus" label="Nueva clase" variant="primary" />
        </div>
        <div className="col-6 col-md-4">
          <QuickAction to={`${base}/agenda`} icon="mdi-calendar-clock" label="Mi agenda" />
        </div>
        <div className="col-6 col-md-4">
          <QuickAction to={`${base}/mis-horas`} icon="mdi-clock-outline" label="Mis horas" />
        </div>
      </div>
    </div>
  );
}

export { TeacherDashboard };
